/* Signup popup — delay 5000ms (measured), all pages, overlay fade 300ms.
   Close = minimise to teaser ("<offer> OFF" tab) and store `<id>-minimized-in-session`;
   closing the teaser stores `<id>-dismissed-in-session` (localStorage, like the reference).
   Submission is the native Shopify customer form POST (challenge/captcha safe); the page that
   Shopify renders afterwards reports posted/error state back through [data-popup-state]. */
(() => {
  class YvPopup extends HTMLElement {
    connectedCallback() {
      this.id_ = this.dataset.popupId || 'yv-popup';
      this.delay = parseInt(this.dataset.delay, 10);
      if (Number.isNaN(this.delay)) this.delay = 5000;
      this.dialog = this.querySelector('.yv-popup');
      this.teaser = this.querySelector('.yv-popup-teaser');
      this.form = this.querySelector('form');
      this.error = this.querySelector('[data-popup-error]');
      this.minKey = `${this.id_}-minimized-in-session`;
      this.disKey = `${this.id_}-dismissed-in-session`;
      this.sucKey = `${this.id_}-submitted`;
      this.pendKey = `${this.id_}-pending`;
      if (!this.dialog) return;
      if (window.Shopify && Shopify.designMode && this.dataset.preview !== 'true') return;

      this.querySelectorAll('[data-popup-close]').forEach((b) => b.addEventListener('click', () => this.minimise()));
      this.querySelector('.yv-popup__overlay')?.addEventListener('click', () => this.minimise());
      this.querySelector('[data-teaser-open]')?.addEventListener('click', () => this.open());
      this.querySelector('[data-teaser-close]')?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.dismiss();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.dialog.classList.contains('is-open')) this.minimise();
      });
      if (this.form) this.form.addEventListener('submit', (e) => this.submit(e));

      // Result of a native submission (Shopify re-rendered the page).
      const stateEl = this.querySelector('[data-popup-state]');
      const state = stateEl ? stateEl.value : 'idle';
      const posted = state === 'posted' || /[?&]customer_posted=true/.test(window.location.search);
      const pending = this.takePending();
      if (pending && posted) {
        this.success();
        return;
      }
      if (pending && state === 'error') {
        this.open();
        this.dialog.classList.add('has-error');
        return;
      }

      const store = this.read();
      if (store.dismissed || store.submitted) return;
      if (store.minimized) {
        this.showTeaser();
        return;
      }
      this.timer = setTimeout(() => this.open(), this.delay);
    }

    read() {
      try {
        return {
          minimized: !!localStorage.getItem(this.minKey),
          dismissed: !!localStorage.getItem(this.disKey),
          submitted: !!localStorage.getItem(this.sucKey),
        };
      } catch (e) {
        return {};
      }
    }
    write(key, value) {
      try {
        localStorage.setItem(key, value);
      } catch (e) {}
    }
    takePending() {
      try {
        const v = sessionStorage.getItem(this.pendKey);
        sessionStorage.removeItem(this.pendKey);
        return !!v;
      } catch (e) {
        return false;
      }
    }

    open() {
      clearTimeout(this.timer);
      this.hideTeaser();
      this.dialog.classList.add('is-open');
      this.dialog.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('yv-popup-open');
      const first = this.dialog.querySelector(
        this.dialog.classList.contains('is-success') ? '.yv-popup__code, [data-popup-close]' : 'input[type="text"], input[type="email"], button'
      );
      if (first) first.focus({ preventScroll: true });
    }
    close() {
      this.dialog.classList.remove('is-open');
      this.dialog.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('yv-popup-open');
    }
    minimise() {
      this.close();
      if (this.dialog.classList.contains('is-success')) {
        this.write(this.sucKey, '1');
        return;
      }
      this.write(this.minKey, this.id_);
      this.showTeaser();
    }
    dismiss() {
      this.hideTeaser();
      this.write(this.disKey, this.id_);
    }
    showTeaser() {
      if (this.teaser) this.teaser.classList.add('is-visible');
    }
    hideTeaser() {
      if (this.teaser) this.teaser.classList.remove('is-visible');
    }
    success() {
      this.hideTeaser();
      this.dialog.classList.remove('has-error');
      this.dialog.classList.add('is-success');
      const code = this.dialog.querySelector('.yv-popup__code');
      if (code) code.setAttribute('tabindex', '-1');
      this.write(this.sucKey, '1');
      this.open();
    }

    submit(event) {
      const email = this.form.querySelector('input[type="email"]');
      this.dialog.classList.remove('has-error');
      if (!email || !email.value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)) {
        event.preventDefault();
        if (this.error && this.error.dataset.default) this.error.textContent = this.error.dataset.default;
        this.dialog.classList.add('has-error');
        email && email.focus();
        return;
      }
      try {
        sessionStorage.setItem(this.pendKey, '1');
      } catch (e) {}
      const button = this.form.querySelector('button[type="submit"]');
      if (button) button.setAttribute('aria-busy', 'true');
      // Native POST continues: Shopify handles the challenge page and redirects back (return_to).
    }
  }
  if (!customElements.get('yv-popup')) customElements.define('yv-popup', YvPopup);
})();
